/**
 * LandingPage
 * -----------
 * Public marketing hero for the Rage Room Scheduler. Highlights the
 * booking experience and funnels visitors directly into the /book flow.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import useApi from '../hooks/useApi';

const CITY_DISPLAY_NAMES = {
  LA: 'Los Angeles HQ',
  LAX: 'Los Angeles HQ',
  LOSANGELES: 'Los Angeles HQ',
  LOS_ANGELES: 'Los Angeles HQ',
  NY: 'New York Lab',
  NYC: 'New York Lab',
  NEWYORK: 'New York Lab',
  BK: 'Brooklyn Lab',
  BROOKLYN: 'Brooklyn Lab',
  ATX: 'Austin Warehouse',
  AUS: 'Austin Warehouse',
  AUSTIN: 'Austin Warehouse',
  CHI: 'Chicago Loft',
  CHICAGO: 'Chicago Loft',
  SEA: 'Seattle Arcade',
  SEATTLE: 'Seattle Arcade',
};

const PLUTO_LOGO_PATH = `${process.env.PUBLIC_URL || ''}/PlutoTV_Logo_1080x1080_RGB_Plutonium.ai.png`;

const rowsFromResponse = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const getCityDisplayName = (city = '') => {
  const key = city.replace(/\s+/g, '').toUpperCase();
  return CITY_DISPLAY_NAMES[key] || `${city || 'City'} Lab`;
};

const normalizeTimeSlot = (timeSlot) => {
  if (!timeSlot) {
    return '00:00:00';
  }
  return timeSlot.length === 5 ? `${timeSlot}:00` : timeSlot;
};

const buildSessionDate = (date, timeSlot) => {
  if (!date || !timeSlot) {
    return null;
  }
  const normalizedTime = normalizeTimeSlot(timeSlot);
  const candidate = new Date(`${date}T${normalizedTime}`);
  return Number.isNaN(candidate.getTime()) ? null : candidate;
};

const formatSessionWindow = ({ date, timeSlot, timezone }) => {
  if (!date || !timeSlot) {
    return 'Scheduling soon';
  }

  const normalizedTime = normalizeTimeSlot(timeSlot);
  const isoCandidate = `${date}T${normalizedTime}`;

  try {
    const eventDate = new Date(isoCandidate);
    if (Number.isNaN(eventDate.getTime())) {
      return `${date} • ${timeSlot}${timezone ? ` ${timezone}` : ''}`;
    }

    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      timeZone: timezone || 'UTC',
    });

    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
      timeZone: timezone || 'UTC',
      timeZoneName: 'short',
    });

    return `${dateFormatter.format(eventDate)} • ${timeFormatter.format(eventDate)}`;
  } catch {
    return `${date} • ${timeSlot}${timezone ? ` ${timezone}` : ''}`;
  }
};

const transformAvailabilityToEvents = (rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  const rowsByCity = rows.reduce((acc, row) => {
    if (!row?.city) {
      return acc;
    }
    if (!acc[row.city]) {
      acc[row.city] = [];
    }
    acc[row.city].push(row);
    return acc;
  }, {});

  return Object.entries(rowsByCity)
    .map(([city, cityRows]) => {
      const sortedCityRows = [...cityRows].sort((a, b) => {
        const aDate = buildSessionDate(a.date, a.timeSlot);
        const bDate = buildSessionDate(b.date, b.timeSlot);
        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;
        return aDate - bDate;
      });

      const nextSession = sortedCityRows[0];
      if (!nextSession) {
        return null;
      }

      const sessionRows = cityRows.filter(
        ({ date, timeSlot }) => date === nextSession.date && timeSlot === nextSession.timeSlot
      );

      const totalRemaining = sessionRows.reduce(
        (sum, row) =>
          sum + (typeof row.remainingCapacity === 'number' ? row.remainingCapacity : 0),
        0
      );

      const totalCapacity = sessionRows.reduce(
        (sum, row) => sum + (typeof row.maxCapacity === 'number' ? row.maxCapacity : 0),
        0
      );

      const sessionStartsAt = buildSessionDate(nextSession.date, nextSession.timeSlot);

      return {
        id: `${city}-${nextSession.date}-${nextSession.timeSlot}`,
        location: getCityDisplayName(city),
        sessionName:
          nextSession.sessionLabel ||
          (nextSession.sessionNumber ? `Session ${nextSession.sessionNumber}` : 'Upcoming Session'),
        timeDetail: formatSessionWindow(nextSession),
        status:
          totalRemaining > 0 && totalCapacity > 0
            ? `${totalRemaining} of ${totalCapacity} slots open`
            : 'Fully booked',
        highlight:
          nextSession.specialNote ||
          `${nextSession.roomName || 'Multi-room'} • ${sessionRows.length} slot${
            sessionRows.length === 1 ? '' : 's'
          }`,
        startsAt: sessionStartsAt,
      };
    })
    .filter(Boolean)
    .sort((a, b) => {
      if (!a.startsAt && !b.startsAt) return 0;
      if (!a.startsAt) return 1;
      if (!b.startsAt) return -1;
      return a.startsAt - b.startsAt;
    })
    .map(({ startsAt, ...event }) => event);
};

const LandingPage = () => {
  const { rageRoom } = useApi();
  const locationFilter = (process.env.REACT_APP_DEFAULT_LOCATION_ID || '').trim();

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [loadingAvailability, setLoadingAvailability] = useState(true);
  const [availabilityError, setAvailabilityError] = useState(null);

  const fetchAvailability = useCallback(async () => {
    if (!rageRoom?.public?.listAvailability) {
      setAvailabilityError('Availability service is not configured.');
      setAvailabilityRows([]);
      setLoadingAvailability(false);
      return;
    }

    setLoadingAvailability(true);
    setAvailabilityError(null);

    try {
      const today = new Date();
      const startDate = today.toISOString().split('T')[0];
      const rangeEnd = new Date(today);
      rangeEnd.setDate(rangeEnd.getDate() + 14);
      const endDate = rangeEnd.toISOString().split('T')[0];

      const availabilityParams = {
        startDate,
        endDate,
        onlyAvailable: true,
      };

      if (locationFilter) {
        availabilityParams.locationId = locationFilter;
      }

      const response = await rageRoom.public.listAvailability(availabilityParams);
      setAvailabilityRows(rowsFromResponse(response));
    } catch (error) {
      setAvailabilityError(error.message || 'Unable to load live availability right now.');
      setAvailabilityRows([]);
    } finally {
      setLoadingAvailability(false);
    }
  }, [locationFilter]);

  useEffect(() => {
    fetchAvailability();
  }, [fetchAvailability]);

  const upcomingEvents = useMemo(
    () => transformAvailabilityToEvents(availabilityRows),
    [availabilityRows]
  );

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 10, md: 14 },
        backgroundColor: '#000',
        backgroundImage: 'none',
      }}
    >
      <Container maxWidth="lg">
        <Stack spacing={8}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={6}
            alignItems="center"
            justifyContent="space-between"
          >
            <Box flex={1}>
              <Box sx={{ display: "flex", justifyContent: "center", width: "100%"}}>
              <Box
                component="img"
                src={PLUTO_LOGO_PATH}
                alt="Pluto TV logo"
                sx={{
                  height: { xs: 64, sm: 80, md: 120 },
                  width: 'auto',
                  mb: 3,
                  filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.6))',
                }}
              />
              </Box>
              <Typography variant="h4" component="h3" color="secondary" gutterBottom 
                sx={{ marginBottom: "0px", textAlign: "center"}}
              >
                The holidays are brutal.
              </Typography>
              <Typography variant="h4" component="h3" color="primary" gutterBottom sx={{ textAlign: "center"}}>
                Free movies can help.
              </Typography>
              {/*<Typography variant="h5" sx={{ mb: 2 }}>
                Pluto TV invites you to smash, shatter, and stream your stress away with its “Holidays Are Brutal” collection and free “Holiday Rage Rooms”.
              </Typography>*/}
              <Typography variant="h6" component="h6" color="primary" sx={{  textAlign: "center", mb: 2 }}>
                Holiday Rage Room Takeover<br/>
                Thursday Dec. 11
              </Typography>
              {/*<Typography variant="h6" color="text.secondary" sx={{ mb: 1.5 }}>
                Browse live availability, verify your crew, and lock a 20 minute rage room experience courtesy of Pluto TV.
              </Typography>*/}
              <Typography variant="h6" color="text.secondary"  sx={{ fontWeight: "normal", textAlign: "center", mb: 1.5 }}>
                New York | The Ragery <br/>
                Los Angeles | Rage Ground<br/>
                Atlanta | Total Rage ATL<br/>
                Chicago | The Angry Cactus Lounge<br/>
                Washington D.C. | Kraken Penn Quarter<br/>
                Houston | Break Life<br/>
                Dallas–Ft. Worth | The Secret Chambers<br/>
                Philadelphia | Rage Philly<br/>
                Phoenix | Breakthrough Smash Room<br/>
                Raleigh–Durham | Wreck It Rage Room<br/>
              </Typography>
              {/*<Typography variant="subtitle1" sx={{ mb: 4, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Instant bookings. While availability lasts.
              </Typography>*/}
              <Typography variant="subtitle1" sx={{ mb: 4, textTransform: 'uppercase', textAlign: "center", letterSpacing: '0.1em' }}>
                BOOK A RAGE ROOM
                COURTESY OF PLUTO TV
              </Typography>
              <Stack direction={{ 
                  xs: 'column', 
                  sm: 'row', 
                }} 
                sx={{justifyContent: "center"}}
                spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  component={RouterLink}
                  to="/book"
                >
                  Book a Session
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  size="large"
                  component={RouterLink}
                  to="/book"
                >
                  View Availability
                </Button>
              </Stack>
            </Box>
            <Card
              sx={{
                flexBasis: { md: '40%' },
                minWidth: { xs: '100%', md: 360 },
                maxHeight: 460,
              }}
            >
              <CardContent>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ letterSpacing: '0.2em' }}
                >
                  UPCOMING EVENTS
                </Typography>
                <Box
                  sx={{
                    mt: 3,
                    maxHeight: 360,
                    overflowY: 'auto',
                    pr: 1,
                    '&::-webkit-scrollbar': {
                      width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: 'rgba(244,243,248,0.05)',
                      borderRadius: 999,
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: 'rgba(255,242,0,0.45)',
                      borderRadius: 999,
                    },
                  }}
                >
                  {loadingAvailability ? (
                    <Typography color="text.secondary">Loading live availability…</Typography>
                  ) : availabilityError ? (
                    <Typography color="error">{availabilityError}</Typography>
                  ) : upcomingEvents.length === 0 ? (
                    <Typography color="text.secondary">
                      No live sessions are available right now. Check back soon.
                    </Typography>
                  ) : (
                    <Stack spacing={3}>
                      {upcomingEvents.map((event, index) => (
                        <Box key={event.id}>
                          <Typography variant="h5">{event.sessionName}</Typography>
                          <Typography color="text.secondary" sx={{ fontWeight: 600 }}>
                            {event.location}
                          </Typography>
                          <Typography color="text.secondary">{event.timeDetail}</Typography>
                          <Typography sx={{ mt: 1, fontStyle: 'italic' }}>
                            {event.highlight}
                          </Typography>
                          <Chip label={event.status} size="small" color="primary" sx={{ mt: 1 }} />
                          {index < upcomingEvents.length - 1 && (
                            <Divider sx={{ mt: 3, borderColor: 'rgba(244,243,248,0.08)' }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
};

export default LandingPage;
